import React, { ReactNode } from 'react';
import { launchLwa } from '../utils/lwa-utils';
import logger from '../../logger';
import AmplitudeUtils from '../utils/amplitude-utils';
import Loader from './common/Loader';
import { PreAuthnError } from '../utils/errors';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface TransformedError {
  name: string;
  message: string;
  redirectable: boolean;
  statusCode?: number;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error?: TransformedError;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error | any): ErrorBoundaryState {
    let transformedError = {
      name: error.name || 'Error',
      message: 'unknown error',
      redirectable: false,
    };
    if (error instanceof PreAuthnError) {
      transformedError = error.errorDetails;
    }

    return { hasError: true, error: transformedError };
  }

  async componentDidCatch() {
    await AmplitudeUtils.logErrorBoundaryError({ error: this.state.error });
    await this.handleError();
  }

  private encodeErrorDetails(): string {
    const { error } = this.state;
    const jsonString = JSON.stringify(error);
    return btoa(jsonString);
  }

  private async handleError() {
    try {
      const encodedErrorDetails = this.encodeErrorDetails();
      logger.log('Launching LWA with error details');
      if (global.settings?.isAuthnWithLwaClientId) {
        await launchLwa({ error_details: encodedErrorDetails });
      }
    }
    catch (lwaError) {
      logger.log('Failed to launch LWA:', lwaError);
      // If we can't launch LWA, at least log the original error
      logger.error('Original error that caused ErrorBoundary to trigger:', lwaError);
      await AmplitudeUtils.logLaunchLwaError({ error: lwaError });
    }
  }

  render() {
    if (this.state.hasError) {
      return <Loader />;
    }
    return this.props.children;
  }
}
